import React, {Fragment} from 'react';

import {Stack, Typography} from '@mui/material';

import {Snackbar} from 'presentation/components';
import {StyledGrid} from 'presentation/components/Grid';
import {PlanTypeEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';

import {InstabilityError} from 'presentation/components/Location/InstabilityError';
import {Instructions} from 'presentation/components/Location/Instructions';
import {LoadingLocation} from 'presentation/components/Location/LoadingLocation';
import {LocationOptionCard} from 'presentation/components/Location/LocationOptionCard';
import {getButtonStyleByPermission} from 'presentation/utils/getButtonStyleByPermission';

import {useGuideLocation} from './hooks/useGuideLocation';
import {LocationProps} from './types';

const {title, geolocationCard, manualSelectionCard, favoritePlaces, autoLocationPermissionDenied} =
	translations['pt-br'].privateFlow.bookingFlow.location;

export function Location({flowType}: LocationProps) {
	const {
		isLaptop,
		isGeoEnabled,
		permission,
		openInstructionsState,
		setOpenInstructionsState,
		openInstabilityErrorState,
		setOpenInstabilityErrorState,
		showPermissionDeniedSnackbarState,
		setShowPermissionDeniedSnackbarState,
		openGetPositionLoading,
		handleNavToStateSelection,
		handleNavToHealthGuideFavoritePlaces,
		initializeGeoGuideFlow,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
		isUnprocessableError,
		handleOnRetry,
		handleGoHome,
		isFavoritePlacesEnabled,
	} = useGuideLocation(flowType);

	return (
		<Fragment>
			<Stack alignItems="center" mt={4} mx={isLaptop ? 4 : 'auto'}>
				<Stack width={isLaptop ? '100%' : 618} gap={2}>
					<Typography variant="subtitle2" color="text.secondary">
						{title}
					</Typography>
					<StyledGrid.Root>
						{isGeoEnabled ? (
							<StyledGrid.Item sm={12} md={6} lg={6}>
								<LocationOptionCard
									label={geolocationCard}
									onClick={initializeGeoGuideFlow}
									{...getButtonStyleByPermission({state: permission})}
								/>
							</StyledGrid.Item>
						) : null}
						<StyledGrid.Item sm={12} md={6} lg={6}>
							<LocationOptionCard
								iconName="my-location-filled"
								label={manualSelectionCard}
								onClick={handleNavToStateSelection}
							/>
						</StyledGrid.Item>
						{flowType === PlanTypeEnum.HEALTH && isFavoritePlacesEnabled && (
							<StyledGrid.Item sm={12} md={6} lg={6}>
								<LocationOptionCard
									iconName="heart-outline"
									label={favoritePlaces}
									onClick={handleNavToHealthGuideFavoritePlaces}
								/>
							</StyledGrid.Item>
						)}
					</StyledGrid.Root>
				</Stack>
			</Stack>

			<Instructions
				open={openInstructionsState}
				onOpen={() => setOpenInstructionsState(true)}
				onClose={() => setOpenInstructionsState(false)}
				onManualSelection={handleNavToStateSelection}
			/>

			<LoadingLocation open={openGetPositionLoading} />

			<InstabilityError
				open={openInstabilityErrorState}
				positionUnavailableOrTimeout={positionUnavailableOrTimeout}
				geolocationOrPermissionIsNotSupported={geolocationOrPermissionIsNotSupported}
				isUnprocessableError={isUnprocessableError}
				onClose={() => setOpenInstabilityErrorState(false)}
				onRetry={handleOnRetry}
				onGoHome={handleGoHome}
				onManualSelection={handleNavToStateSelection}
			/>

			<Snackbar
				onClose={() => setShowPermissionDeniedSnackbarState(false)}
				message={autoLocationPermissionDenied}
				open={showPermissionDeniedSnackbarState}
			/>
		</Fragment>
	);
}
